import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    TextField,
    Button,
    Typography,
    Snackbar,
    Alert,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Divider,
    Grid,
    RadioGroup,
    Radio,
    FormControlLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    Chip
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';
import Header from '../../Header';
import { local_url, local_signage } from '../../Const';

// Every existing usage of PricingSchemeType in the app is the literal value "HORECA".
const PRICING_SCHEME_TYPES = ['HORECA'];

// Fixed for now: every row is always credit type "LC" (not user-editable).
const FIXED_CREDIT_CUST_TYPE = 'LC';

const ADVANCED_SEARCH_LABELS = { CUSTCODE: 'Customer Code', MOBILE: 'Mobile Number', CUSTNAME: 'Customer Name' };

let rowIdCounter = 0;
const makeEmptyRow = () => ({
    id: `row-${++rowIdCounter}`,
    companyId: '',
    areaId: '',
    pricingSchemeType: PRICING_SCHEME_TYPES[0],
    creditCustType: FIXED_CREDIT_CUST_TYPE
});

export const Customers = () => {
    const token = localStorage.getItem('AuthorizationToken');
    const userId = localStorage.getItem('userId');
    const createdUserId = userId || 'ADMIN';

    // Customer search
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    // Advanced search: Customer Type + Code/Mobile/Name
    const [customerTypes, setCustomerTypes] = useState([]);
    const [loadingCustomerTypes, setLoadingCustomerTypes] = useState(false);
    const [selectedCustomerType, setSelectedCustomerType] = useState('');
    const [advancedSearchType, setAdvancedSearchType] = useState('CUSTCODE');
    const [advancedSearchInput, setAdvancedSearchInput] = useState('');
    const [advancedSearchResults, setAdvancedSearchResults] = useState([]);
    const [loadingAdvancedSearch, setLoadingAdvancedSearch] = useState(false);

    // Company + Area lists for the dropdowns
    const [companies, setCompanies] = useState([]);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    const [areas, setAreas] = useState([]);
    const [loadingAreas, setLoadingAreas] = useState(false);

    // Company / Area / Pricing Type / Credit Cust Type rows
    const [rows, setRows] = useState([makeEmptyRow()]);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

    const showNotification = (message, severity = 'success') => {
        setNotification({ open: true, message, severity });
    };

    const handleCloseNotification = () => {
        setNotification((prev) => ({ ...prev, open: false }));
    };

    const handleAuthError = () => {
        showNotification('Session expired. Please log in again.', 'error');
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = '/Login';
    };

    // Load the Company dropdown once, on mount
    useEffect(() => {
        const fetchCompanies = async () => {
            if (!token) return;
            setLoadingCompanies(true);
            try {
                const response = await fetch(`${local_url}/api/Company/GetCompanyList`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (!response.ok) throw new Error('Failed to fetch companies');
                const data = await response.json();
                setCompanies(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error('Error fetching companies:', error);
                showNotification('Could not load the Company list.', 'error');
            } finally {
                setLoadingCompanies(false);
            }
        };
        fetchCompanies();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load the Area dropdown once, on mount
    useEffect(() => {
        const fetchAreas = async () => {
            if (!token || !userId) return;
            setLoadingAreas(true);
            try {
                const response = await fetch(`${local_url}/api/SFArea/${userId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (!response.ok) throw new Error('Failed to fetch areas');
                const data = await response.json();
                setAreas(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error('Error fetching areas:', error);
                showNotification('Could not load the Area list.', 'error');
            } finally {
                setLoadingAreas(false);
            }
        };
        fetchAreas();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load the Customer Type dropdown once, on mount
    useEffect(() => {
        const fetchCustomerTypes = async () => {
            if (!token) return;
            setLoadingCustomerTypes(true);
            try {
                const response = await fetch(`${local_url}/api/CustomerList/GetCustomerTypes/C`, {
                    headers: { Authorization: `Bearer ${token}` },
                    cache: 'no-store'
                });
                if (!response.ok) throw new Error('Failed to fetch customer types');
                const data = await response.json();
                setCustomerTypes(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error('Error fetching customer types:', error);
                showNotification('Could not load the Customer Type list.', 'error');
            } finally {
                setLoadingCustomerTypes(false);
            }
        };
        fetchCustomerTypes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Advanced search: look up customers by Customer Type + Code/Mobile/Name
    const handleAdvancedSearch = async () => {
        if (!selectedCustomerType || !advancedSearchInput.trim()) {
            showNotification('Please select a Customer Type and enter a search term.', 'error');
            return;
        }
        if (!token) {
            handleAuthError();
            return;
        }
        setLoadingAdvancedSearch(true);
        try {
            const response = await fetch(
                `${local_url}/api/POSCustomerSerch/Get/${selectedCustomerType}/${advancedSearchType}/${encodeURIComponent(advancedSearchInput.trim())}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error(`Search failed with status ${response.status}`);
            const data = await response.json();
            setAdvancedSearchResults(Array.isArray(data?.customers) ? data.customers : []);
        } catch (error) {
            console.error('Error running advanced customer search:', error);
            showNotification('Customer search failed.', 'error');
            setAdvancedSearchResults([]);
        } finally {
            setLoadingAdvancedSearch(false);
        }
    };

    const handleSelectAdvancedResult = (customer) => {
        setSelectedCustomer(customer);
        setAdvancedSearchResults([]);
    };

    const getCustomerLabel = (customer) => {
        if (!customer) return '';
        const code = customer.customerCode || customer.custID || '';
        const name = customer.customerName || '';
        return code && name ? `${code}-${name}` : name || code;
    };

    const getCustomerId = (customer) => customer?.customerCode || customer?.custID || '';

    const updateRow = (rowId, changes) => {
        setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, ...changes } : r)));
    };

    const handleAddRow = () => {
        setRows((prev) => [...prev, makeEmptyRow()]);
    };

    const handleDeleteRow = (rowId) => {
        setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== rowId) : prev));
    };

    const isRowComplete = (row) =>
        row.companyId && row.areaId && row.pricingSchemeType && row.creditCustType.trim() !== '';

    const isReadyToSave = !!selectedCustomer && rows.some(isRowComplete);

    const handleSubmit = async () => {
        if (!selectedCustomer) {
            showNotification('Please search for and select a customer first.', 'error');
            return;
        }
        const validRows = rows.filter(isRowComplete);
        if (validRows.length === 0) {
            showNotification('Please complete at least one row (Company, Area, Pricing Type, Credit Cust Type).', 'error');
            return;
        }
        if (!token) {
            handleAuthError();
            return;
        }

        setIsSubmitting(true);

        const payload = {
            CustomerId: getCustomerId(selectedCustomer),
            CreatedUserId: createdUserId,
            Areas: validRows.map((row) => ({
                CompanyId: row.companyId,
                AreaID: row.areaId,
                PricingSchemeType: row.pricingSchemeType,
                CreditCustType: row.creditCustType
            }))
        };

        try {
            const response = await fetch(`${local_signage}/api/v1/insertcustomerbillingareas`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                // 401/403 here is a red herring for "session expired": logging in fresh and
                // retrying still fails, so it's the backend rejecting this specific request
                // (permissions/role on this endpoint), not an expired token. Surface it
                // instead of forcing a logout.
                let serverMessage = '';
                try {
                    const errorBody = await response.json();
                    serverMessage = errorBody?.message || '';
                } catch {
                    // response body wasn't JSON; fall back to the generic message below
                }
                throw new Error(serverMessage || `Request failed with status ${response.status}`);
            }

            // The server can respond 200 OK but still report a business-level failure
            // (e.g. an invalid CustomerId), so we must check the "success" flag in the body.
            const result = await response.json();

            if (result.success === false) {
                showNotification(result.message || 'The server rejected this request.', 'error');
                return;
            }

            showNotification(result.message || `Saved ${validRows.length} area assignment(s) successfully!`, 'success');
            setRows([makeEmptyRow()]);
        } catch (error) {
            console.error('Error saving customer billing areas:', error);
            showNotification(error.message || 'Failed to save. Please try again.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <Header />
            <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1000, mx: 'auto' }}>
                <Card sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                    <CardHeader
                        avatar={<PersonIcon sx={{ color: '#004d00' }} />}
                        title={
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#004d00' }}>
                                Customer Billing Area
                            </Typography>
                        }
                    />
                    <CardContent>
                        {/* Advanced search: Customer Type + Code / Mobile / Name */}
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                            Search Customer
                        </Typography>
                        <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Customer Type</InputLabel>
                                    <Select
                                        label="Customer Type"
                                        value={selectedCustomerType}
                                        onChange={(e) => setSelectedCustomerType(e.target.value)}
                                        disabled={loadingCustomerTypes}
                                    >
                                        {customerTypes.map((type) => (
                                            <MenuItem key={type.customerTypeCode} value={type.customerTypeCode}>
                                                {type.customerTypeName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        <Grid container spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                            <Grid item xs={12} sm={4}>
                                <RadioGroup
                                    row
                                    value={advancedSearchType}
                                    onChange={(e) => setAdvancedSearchType(e.target.value)}
                                >
                                    <FormControlLabel value="CUSTCODE" control={<Radio size="small" />} label="Code" />
                                    <FormControlLabel value="MOBILE" control={<Radio size="small" />} label="Mobile" />
                                    <FormControlLabel value="CUSTNAME" control={<Radio size="small" />} label="Name" />
                                </RadioGroup>
                            </Grid>
                            <Grid item xs={12} sm={8}>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label={ADVANCED_SEARCH_LABELS[advancedSearchType]}
                                        value={advancedSearchInput}
                                        onChange={(e) => setAdvancedSearchInput(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleAdvancedSearch();
                                        }}
                                    />
                                    <Button
                                        variant="contained"
                                        onClick={handleAdvancedSearch}
                                        disabled={loadingAdvancedSearch}
                                        sx={{ minWidth: 44, px: 0, bgcolor: '#004d00', '&:hover': { bgcolor: '#003300' } }}
                                    >
                                        {loadingAdvancedSearch ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>

                        <Dialog
                            open={advancedSearchResults.length > 0}
                            onClose={() => setAdvancedSearchResults([])}
                            maxWidth="md"
                            fullWidth
                            PaperProps={{ sx: { borderRadius: '12px' } }}
                        >
                            <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
                                <PersonSearchIcon sx={{ mr: 1, color: '#004d00' }} /> Select Customer
                                <IconButton onClick={() => setAdvancedSearchResults([])} sx={{ ml: 'auto', color: 'text.secondary' }}>
                                    <CloseIcon />
                                </IconButton>
                            </DialogTitle>
                            <DialogContent>
                                <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                                    {advancedSearchResults.map((customer) => (
                                        <Grid item xs={12} sm={6} md={4} key={getCustomerId(customer)}>
                                            <Card
                                                onClick={() => handleSelectAdvancedResult(customer)}
                                                sx={{
                                                    border: '1px solid #e0e0e0',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': { transform: 'translateY(-2px)', boxShadow: 3, borderColor: '#004d00' }
                                                }}
                                            >
                                                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                    <Typography variant="subtitle2" noWrap sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <PersonIcon sx={{ mr: 0.5, fontSize: '16px' }} />
                                                        {customer.customerName || '-'}
                                                    </Typography>
                                                    <Chip
                                                        icon={<PhoneIcon sx={{ fontSize: '14px' }} />}
                                                        label={customer.mobile || 'N/A'}
                                                        size="small"
                                                        sx={{ mt: 1, fontSize: '0.75rem', backgroundColor: '#e0e0e0' }}
                                                    />
                                                    <Chip
                                                        label={`ID: ${getCustomerId(customer) || 'N/A'}`}
                                                        size="small"
                                                        sx={{ mt: 1, ml: 1, fontSize: '0.75rem', backgroundColor: '#004d00', color: 'white' }}
                                                    />
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </DialogContent>
                        </Dialog>

                        <TextField
                            fullWidth
                            size="small"
                            label="Customer Name"
                            placeholder="Search Code / Mobile / Name above to select a customer"
                            value={selectedCustomer ? getCustomerLabel(selectedCustomer) : ''}
                            InputProps={{ readOnly: true }}
                        />

                        <Divider sx={{ my: 3 }} />

                        {/* Company / Area / Pricing Type / Credit Cust Type table */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Billing Areas
                            </Typography>
                            <Button size="small" startIcon={<AddIcon />} onClick={handleAddRow}>
                                Add Row
                            </Button>
                        </Box>

                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                        <TableCell sx={{ fontWeight: 700 }}>Company</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>AREA</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Pricing Type</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Credit Cust Type</TableCell>
                                        <TableCell sx={{ width: 48 }} />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((row) => (
                                        <TableRow key={row.id}>
                                            <TableCell sx={{ minWidth: 160 }}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Company</InputLabel>
                                                    <Select
                                                        label="Company"
                                                        value={row.companyId}
                                                        onChange={(e) => updateRow(row.id, { companyId: e.target.value })}
                                                        disabled={loadingCompanies}
                                                    >
                                                        {companies.map((company) => (
                                                            <MenuItem key={company.entityCode} value={company.entityCode}>
                                                                {company.entityName}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </TableCell>
                                            <TableCell sx={{ minWidth: 160 }}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Area</InputLabel>
                                                    <Select
                                                        label="Area"
                                                        value={row.areaId}
                                                        onChange={(e) => updateRow(row.id, { areaId: e.target.value })}
                                                        disabled={loadingAreas}
                                                    >
                                                        {areas.map((area) => (
                                                            <MenuItem key={area.sfAreaId} value={area.sfAreaId}>
                                                                {area.sfAreaName}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </TableCell>
                                            <TableCell sx={{ minWidth: 150 }}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Pricing Type</InputLabel>
                                                    <Select
                                                        label="Pricing Type"
                                                        value={row.pricingSchemeType}
                                                        onChange={(e) => updateRow(row.id, { pricingSchemeType: e.target.value })}
                                                    >
                                                        {PRICING_SCHEME_TYPES.map((type) => (
                                                            <MenuItem key={type} value={type}>
                                                                {type}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </TableCell>
                                            <TableCell sx={{ minWidth: 140 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    value={row.creditCustType}
                                                    InputProps={{ readOnly: true }}
                                                    sx={{
                                                        bgcolor: '#f5f5f5',
                                                        '& .MuiInputBase-input': {
                                                            color: '#000',
                                                            WebkitTextFillColor: '#000'
                                                        }
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteRow(row.id)}
                                                    disabled={rows.length === 1}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                            <Button
                                variant="contained"
                                startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                                onClick={handleSubmit}
                                disabled={isSubmitting || !isReadyToSave}
                                sx={{
                                    bgcolor: '#004d00',
                                    '&:hover': { bgcolor: '#003300' },
                                    minWidth: 140
                                }}
                            >
                                {isSubmitting ? 'Saving...' : 'Save'}
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            </Box>

            <Snackbar
                open={notification.open}
                autoHideDuration={4000}
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
                    {notification.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default Customers;
